import { ImagePaths } from '../../lib/imageUrls.js';

export const purviewInsiderRiskCommsCompliance = {

    id: 41,
    slug: 'purview-insider-risk-comms-compliance',
    ogImage: '/og/courses/purview-insider-risk-comms-compliance.jpg',
    courseCode: 'PURVIEW-IRCC-001',
    featured: true,
    title: 'Purview for Insider Risk Management',
    subtitle: 'Detect insider threats, manage cases, and prove regulatory diligence',
    organization: 'EDUC4TE',
    valueProposition: 'Stand up a defensible Insider Risk Management programme in Microsoft Purview that detects risky human behaviour, integrates HR connector risk scoring with Adaptive Protection, and produces the evidence pack the OAIC, APRA, and Fair Work regulators expect under Tranche 1 enforcement.',
    duration: '8 hours',
    students: 'New',
    level: 'Intermediate',
    image: ImagePaths.COURSE_4,
    shortDescription: 'PURVIEW-IRCC-001 — Purview Insider Risk Management covering HR connector, case management, and Adaptive Protection for security and HR teams.',
    price: {
      aud: 1000,
      currency: 'AUD'
    },
    stripeConfig: {
    priceId: 'price_1Te5C3Ku1nyc322UuBCcrvmX',
    productId: 'prod_Uapkb3ocfCWLJI'
  },

    snapshot: {
      whoItsFor: [
        'Insider risk analysts operating Microsoft Purview Insider Risk Management at enterprise scale',
        'Joint HR and IT compliance teams responsible for employee monitoring under the Fair Work Act',
        'Security operations leads triaging and investigating insider risk alerts',
        'Legal and employee-relations teams preparing OAIC, APRA CPS 234, and Tranche 1 evidence packs'
      ],
      accreditation: 'Aligned to Microsoft Purview Insider Risk Management and Adaptive Protection learning paths. Fills the gap left by the reduced coverage of insider risk in the SC-401 associate exam.'
    },

    logistics: {
      classSize: 'One-day intensive — virtual cohort with hands-on labs, joint HR/IT scenarios, and live Q&A',
      requirements: [
        'Working knowledge of Microsoft 365 (Exchange, Teams, SharePoint, OneDrive)',
        'Familiarity with Microsoft Purview compliance portal and role groups',
        'Access to a Microsoft 365 E5 (or Compliance add-on) tenant for labs',
        'Awareness of Australian privacy and employee-monitoring obligations',
        'Stakeholder sign-off for insider risk activities (HR partnership confirmed)'
      ]
    },

    overview: 'Purview for Insider Risk Management is a one-day intensive that takes insider risk, HR, legal, and security operations practitioners from policy design through to a working operating model that holds up under OAIC investigation, APRA CPS 234 review, and Fair Work scrutiny. You will configure Insider Risk Management policy templates, sequence detection, and risk levels; integrate the HR connector for automated risk scoring; triage and investigate alerts through complete case management workflows; collect and preserve evidence from alert to resolution under the Australian Privacy Act; and deploy Adaptive Protection to auto-graduate dynamic DLP controls based on real-time user risk scores. The programme is grounded in Microsoft Learn guidance updated through 2025 and 2026, OAIC Notifiable Data Breaches scheme guidance, and the Fair Work Act constraints on employee monitoring.',

    outcomes: [
      'Configure Insider Risk Management policy templates and integrate the HR connector for automated risk scoring',
      'Triage and investigate insider risk alerts through complete case management workflows',
      'Collect and preserve evidence from alert to resolution under the Australian Privacy Act',
      'Deploy Adaptive Protection dynamic DLP based on real-time user risk scores',
      'Integrate Insider Risk Management with Microsoft Defender for Endpoint for enriched behavioural signals',
      'Configure priority user groups and sequence detection for advanced threat patterns'
    ],

    audience: [
      'Insider risk analysts and security operations leads',
      'Joint HR and IT compliance teams',
      'Investigators and case managers',
      'Legal, privacy, and employee-relations teams'
    ],

    prerequisites: [
      'Practical Microsoft 365 administration or compliance experience',
      'Exposure to the Microsoft Purview compliance portal and role groups',
      'Awareness of Australian privacy law and Fair Work obligations',
      'Stakeholder mandate to operate insider risk policies',
      'Completion of EDUC4TE Purview Track Module 2, or equivalent experience'
    ],

    modules: [
      {
        number: 1,
        title: 'Insider Risk Management foundations',
        duration: '2 hours',
        topics: [
          'Insider Risk Management policy templates: data theft, data leaks, and security policy violations',
          'HR connector integration for automated risk scoring',
          'Sequence detection and how risk levels (low, moderate, elevated) are calculated',
          'Role-based access design across Insider Risk Management, Investigators, Auditors, and Analysts groups',
          'Privacy-by-design defaults: anonymised usernames, dual-authorisation, and Fair Work procedural fairness requirements'
        ]
      },
      {
        number: 2,
        title: 'Case management and investigation',
        duration: '3 hours',
        topics: [
          'Triaging and investigating insider risk alerts',
          'Complete case management workflows from alert to resolution',
          'Evidence collection and preservation in compliance with the Australian Privacy Act',
          'Escalation to eDiscovery for matters requiring legal hold',
          'Reviewer documentation and procedural fairness under the Fair Work Act'
        ]
      },
      {
        number: 3,
        title: 'Advanced Insider Risk Management',
        duration: '3 hours',
        topics: [
          'Adaptive Protection: dynamic DLP based on real-time user risk scores',
          'Integration with Microsoft Defender for Endpoint for enriched behavioural signals',
          'Priority user groups for heightened monitoring of sensitive roles',
          'Sequence detection for advanced threat patterns',
          'Reference: Microsoft Learn — Insider Risk Management (updated 2025/2026)'
        ]
      }
    ],

    format: {
      type: 'One-day intensive (virtual), or lunch-and-learn (8 x 1-hour sessions over 8 days)',
      includes: [
        '8 hours of instructor-led training across one day',
        'Hands-on labs in a Microsoft 365 compliance tenant with Insider Risk Management enabled',
        'Joint HR and IT scenario walkthroughs covering procedural fairness and dual-authorisation',
        'Case management and investigation workflow walkthroughs from alert to resolution',
        'OAIC defensibility evidence pack template aligned to Tranche 1 enforcement',
        'Adaptive Protection dynamic DLP integration playbook',
        'Certificate of completion suitable for CPD claims'
      ]
    },

    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Register interest for the next one-day intensive — small cohort to support hands-on labs and joint HR/IT scenarios'
    },

    certification: 'Educ4te Certificate of Completion — Purview for Insider Risk Management. Fills the curriculum gap left by the retirement of SC-400 and the reduced insider risk coverage of SC-401.',

    faqs: [
      {
        question: 'Do I need a Microsoft 365 E5 tenant to follow the labs?',
        answer: 'Yes — Insider Risk Management and Adaptive Protection require a Microsoft 365 E5 licence or the Compliance add-on (with the Insider Risk Management add-on where applicable). A developer tenant with the Compliance add-on is sufficient for the labs.'
      },
      {
        question: 'Why is this content not fully covered by SC-401?',
        answer: 'Microsoft retired SC-400 and replaced it with the SC-401 Administering Information Security in Microsoft 365 associate exam, which reduced its coverage of Insider Risk Management. This module exists to provide the depth on insider risk that the current Microsoft official curriculum no longer delivers.'
      },
      {
        question: 'Why does the course insist on HR partnership before deployment?',
        answer: 'Insider Risk Management monitors employee behaviour. Under the Fair Work Act, state surveillance legislation, and APP 11, employee monitoring requires lawful basis, notice, procedural fairness, and (in most cases) HR co-ownership. Deploying without HR sign-off creates legal exposure and undermines the defensibility of any subsequent investigation.'
      },
      {
        question: 'How does the course handle Fair Work Act employee-monitoring constraints?',
        answer: 'Every monitoring control is paired with the lawful basis, notice, and procedural fairness pattern required under the Fair Work Act and applicable state surveillance legislation. We walk through anonymised investigation workflows, dual-authorisation, and the documentation an employee-relations team needs if a matter escalates.'
      },
      {
        question: 'How do Tranche 1 and Tranche 2 privacy reforms affect this programme?',
        answer: 'Tranche 1 (2024) introduced a statutory tort for serious invasions of privacy and expanded OAIC enforcement powers — the first compliance sweeps launched in December 2025. Tranche 2 is progressing with a fair-and-reasonable test, redefined personal information and consent, and the likely removal of the small business exemption. The evidence pack you build is designed to satisfy current Tranche 1 enforcement and to be forward-compatible with Tranche 2.'
      },
      {
        question: 'Is the course eligible for CPD?',
        answer: 'Yes — the 8 hours of instructor-led training and accompanying evidence pack are suitable for CPD claims with professional bodies including ACS, IAPP, AISA, and the Governance Institute of Australia. A certificate of completion is issued on successful participation.'
      },
      {
        question: 'Can this course be delivered as lunch-and-learn sessions?',
        answer: 'Yes. The course runs as eight one-hour sessions over eight days, with the same content and outcomes as the one-day format, paced around the working day so people do not need to be away from their desks for a full day. Delivery is live online or on-site, and private cohorts are available for teams. Hands-on labs are spread across the sessions, so participants need access to the lab environment throughout. Contact us to arrange a schedule.'
      }
    ],

    australianContext: true,
    complianceFrameworks: ['Privacy Act 1988', 'OAIC APP 11', 'Notifiable Data Breaches scheme', 'APRA CPS 234', 'Fair Work Act']
};
